import { AlertCircle, Loader2, Upload } from 'lucide-react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useDocumentUpload } from '../hooks/use-document-upload'

interface DocumentUploadProps {
  onUploadComplete?: (documentId: string, file: File) => void
  maxSize?: number // in bytes
  accept?: Record<string, string[]>
  multiple?: boolean
  labels?: {
    singular?: string
    plural?: string
  }
}

export function DocumentUpload({
  onUploadComplete,
  maxSize = 50 * 1024 * 1024, // 50MB default
  accept = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
  },
  multiple = false,
  labels,
}: DocumentUploadProps) {
  const { upload, isUploading } = useDocumentUpload({
    onSuccess: onUploadComplete,
  })

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      for (const file of acceptedFiles) {
        await upload(file)
      }
    },
    [upload]
  )

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxSize,
    accept,
    multiple,
    disabled: isUploading,
  })

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`
  }

  const label = (multiple ? labels?.plural : labels?.singular) ?? 'files'

  return (
    <div className="w-full space-y-4">
      <div
        {...getRootProps()}
        className={`
          relative rounded-lg border-2 border-dashed p-8 text-center cursor-pointer
          transition-colors duration-200
          ${isUploading ? 'border-primary/50 bg-primary/5 cursor-not-allowed' : ''}
          ${isDragActive ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}
        `}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          {isUploading ? (
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          ) : (
            <Upload className="w-10 h-10 text-muted-foreground" />
          )}
          <div className="space-y-1">
            <p className="text-sm font-medium text-white">
              {isUploading
                ? 'Uploading...'
                : isDragActive
                  ? `Drop the ${label} here...`
                  : `Drag & drop ${label} here, or click to select`}
            </p>
            <p className="text-xs text-muted-foreground">Max size: {formatFileSize(maxSize)}</p>
          </div>
        </div>
      </div>

      {fileRejections.length > 0 && (
        <div className="rounded-lg bg-red-900/20 border border-red-800 p-4 space-y-2">
          {fileRejections.map(({ file, errors }) => (
            <div key={file.name} className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-red-200">{file.name}</p>
                {errors.map((e) => (
                  <p key={e.code} className="text-red-400">
                    {e.message}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
